import React, { useCallback, useState, useEffect } from "react";

import { doc, setDoc, getDocs, Timestamp, where, collection, query, getDoc, arrayUnion, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { Employee, TimeEvent } from "../../types/employee";
import { EmployeeSchedule } from "../../types/employee";
import { updateAccruedMinutesForDate } from "../../utils/accrual";
import { getClockInAccess } from "../../utils/deviceIdentity";
import { getScheduleLocationLabel } from "../../utils/scheduleLocations";

import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import weekday from "dayjs/plugin/weekday";
import Snackbar from '@mui/material/Snackbar';

import { PinDialog } from "../../components/PinDialog/PinDialog";
import { SelectEmployeeDialog } from "../../components/SelectEmployeeDialog/SelectEmployee"


dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);

type ScheduleMap = {
    [employeeId: string]: {
        [date: string]: EmployeeSchedule;
    };
};

type KioskTimeAction = "clock_in" | "break_start" | "break_end" | "clock_out";
type KioskMessageKind = "success" | "error";

const locationSuccessMessages: Record<string, Record<KioskTimeAction, string[]>> = {
    "In Store - Castle": {
        clock_in: [
            "The gates are open. Welcome to the Castle!",
            "You’ve crossed the drawbridge—have a great shift!",
            "Clocked in. The Castle is in good hands.",
        ],
        break_start: [
            "The royal court has approved your break.",
            "A quick retreat from the Castle—enjoy your break!",
            "Break time. Even the Castle crew needs to recharge.",
        ],
        break_end: [
            "Back through the gates—let’s do this!",
            "The Castle welcomes you back from break.",
            "Break complete. Back to defending the Castle!",
        ],
        clock_out: [
            "Castle secured. Enjoy your time off!",
            "The drawbridge is up—you’re clocked out!",
            "Another day at the Castle is in the books.",
        ],
    },
    "In Store - West wing": {
        clock_in: [
            "You’ve made it to the West wing. Have a great shift!",
            "The compass says: right place, right on time.",
            "Clocked in and heading west!",
        ],
        break_start: [
            "The compass is pointing toward break time.",
            "Taking a quick breather in the West wing.",
            "West wing break mode: activated.",
        ],
        break_end: [
            "Compass reset—you’re back from break!",
            "Back on course in the West wing.",
            "Break complete. Westward we go!",
        ],
        clock_out: [
            "Leaving the West wing—safe travels!",
            "Clocked out. The compass points home.",
            "That’s a wrap from the West wing!",
        ],
    },
};

// this component displays a calander like view and is only accessible by directly going to the route /Kiosk
// in the calendar view employees can click on their name if they are scheduled for today to clock in, out etc
export const Kiosk: React.FC = () => {

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [employeeScheduleMap, setEmployeeScheduleMap] = useState<ScheduleMap>({});
    const [clockInEmployee, setClockInEmployee] = useState("");
    const [fullName, setFullName] = useState("");
    const [message, setMessage] = useState("");
    const [messageTitle, setMessageTitle] = useState("");
    const [messageKind, setMessageKind] = useState<KioskMessageKind>("success");
    const [currentStatus, setCurrentStatus] = useState("");
    const [notScheduled, setNotScheduled] = useState(false);
    const [currentTime, setCurrentTime] = useState(() => dayjs());
    const [scheduleLoading, setScheduleLoading] = useState(true);

    const showKioskMessage = useCallback((
        text: string,
        title: string,
        kind: KioskMessageKind
    ) => {
        setMessage(text);
        setMessageTitle(title);
        setMessageKind(kind);
    }, []);

    //this defaults to the sunday before the current date and then doees 27 days aka 4 weeks
    const [dateRange] = React.useState<{
        startDate: Dayjs | null;
        endDate: Dayjs | null;
    }>(() => {
        const today = dayjs();
        const startOfWeek = today.startOf("week"); // Sunday or Monday depending on locale
        const endOfRange = today.add(3, "week").endOf("week"); // 3 weeks from today, end of that week

        return {
            startDate: startOfWeek,
            endDate: endOfRange,
        };
    });


    //get our employees for data blends with schedule
    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const snapshot = await getDocs(collection(db, "employees"));
                let employeesList: Employee[] = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as Employee[];
                employeesList = employeesList.filter(emp => !emp.archived) // only active employees
                setEmployees(employeesList);
                if (employeesList.length === 0) setScheduleLoading(false);
            } catch (error) {
                console.error("Error fetching employees:", error);
                setScheduleLoading(false);
            }
        };

        fetchEmployees();
    }, []);

    useEffect(() => {
        const timer = window.setInterval(() => setCurrentTime(dayjs()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    //helper function to get all schedules
    const getAllSchedulesForRange = useCallback(async (startDate: Date, endDate: Date): Promise<EmployeeSchedule[]> => {
        const start = startDate.toISOString().split("T")[0];
        const end = endDate.toISOString().split("T")[0];

        const q = query(
            collection(db, "schedules"),
            where("date", ">=", start),
            where("date", "<=", end)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => doc.data() as EmployeeSchedule);
    }, []);

    //function to populate our state map for schedule display
    const fetchAllSchedules = useCallback(async (employeeList: Employee[]) => {
        if (!dateRange.startDate || !dateRange.endDate) return;

        const start = dateRange.startDate.toDate();
        const end = dateRange.endDate.toDate();

        setScheduleLoading(true);

        try {
            const allSchedules = await getAllSchedulesForRange(start, end);

            const scheduleMap: ScheduleMap = {};
            const scheduledMinutesMap: Record<string, number> = {};

            // Initialize empty objects for each employee to ensure all employees appear in the map
            employeeList.forEach(emp => {
                scheduleMap[emp.id] = {};
                scheduledMinutesMap[emp.id] = 0;
            });

            allSchedules.forEach((entry) => {
                const userId = entry.userId;
                if (!scheduleMap[userId]) {
                    scheduleMap[userId] = {};
                    scheduledMinutesMap[userId] = 0;
                }

                scheduleMap[userId][entry.date] = entry;

                if (entry.shiftStart && entry.shiftEnd) {
                    const startDate = entry.shiftStart.toDate();
                    const endDate = entry.shiftEnd.toDate();
                    const minutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
                    scheduledMinutesMap[userId] = (scheduledMinutesMap[userId] || 0) + minutes;
                }
            });

            setEmployeeScheduleMap(scheduleMap);
        } catch (error) {
            console.error("Error fetching schedules:", error);
            showKioskMessage(
                "The schedule could not be loaded. Please refresh and try again.",
                "Schedule unavailable",
                "error"
            );
        } finally {
            setScheduleLoading(false);
        }

    }, [dateRange.endDate, dateRange.startDate, getAllSchedulesForRange, showKioskMessage]);

    //get all schedyles and set state variables
    useEffect(() => {
        if (employees.length && dateRange.startDate && dateRange.endDate) {
            fetchAllSchedules(employees);
        }
    }, [dateRange.startDate, dateRange.endDate, employees, fetchAllSchedules]);

    //this builds our array of dates to display on the calendar
    const getDatesInRange = (start: Dayjs | null, end: Dayjs | null): Dayjs[] => {
        if (!start || !end) return [];

        const dates: Dayjs[] = [];
        let current = start.startOf('day');

        while (current.isSameOrBefore(end, 'day')) {
            dates.push(current);
            current = current.add(1, 'day');
        }

        return dates;
    };

    const dateList = getDatesInRange(dateRange.startDate, dateRange.endDate);

    const formatDate = (date: Timestamp) => {
        const jsDate = date.toDate();
        const hour = jsDate.getHours();
        const minute = jsDate.getMinutes();

        const twelveHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

        return minute === 0
            ? `${twelveHour}`
            : `${twelveHour}:${minute.toString().padStart(2, "0")}`;
    };

    const handleSubmitPin = (action: String, pin: String) => {

        for (let emp of employees) {
            if (emp.id === clockInEmployee) {
                if (pin === emp.pin) {
                    //foiund the employee thats taking these actions
                    //lets submit a time entry with the action and current time if the pin matches
                    if (action === "Clock In") {
                        clockIn(emp.id)
                    }
                    if (action === "Clock Out") {
                        clockOut(emp.id)
                    }
                    if (action === "Take a Break") {
                        takeBreak(emp.id)
                    }
                    if (action === "Get Back to Work") {
                        endBreak(emp.id)
                    }
                } else {
                    showKioskMessage(
                        "Incorrect PIN...who are you trying to hack?",
                        "That PIN didn’t match",
                        "error"
                    )
                }

            }
        }
    }

    const handleOpenPinDialog = (emp: Employee) => {
        setCurrentStatus("Checking status…")
        getCurrentStatusByEmployee(emp.id)
        setClockInEmployee(emp.id)
        let fullName = emp.firstName + " " + emp.lastName
        setFullName(fullName)
    }

    const getCurrentStatusByEmployee = async (id: string) => {
        const docRef = getTodayDocRef(id);

        try {
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                setCurrentStatus("Clocked Out");
                return;
            }

            const data = docSnap.data();
            const events: TimeEvent[] = data?.events || [];

            if (events.length === 0) {
                setCurrentStatus("Clocked Out");
                return;
            }

            // Find the most recent event
            const latestEvent = events
                .slice()
                .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis())[0];

            // Map event type to human-friendly status
            switch (latestEvent.type) {
                case "clock_in":
                    setCurrentStatus("Clocked In");
                    break;
                case "clock_out":
                    setCurrentStatus("Clocked Out");
                    break;
                case "break_start":
                    setCurrentStatus("On Break");
                    break;
                case "break_end":
                    setCurrentStatus("Clocked In");
                    break;
                default:
                    setCurrentStatus("Clocked Out");
            }

        } catch (error) {
            alert("Failed to get current status");
        }
    };


    const handleClose = () => {
        setMessage('')
    }

    // functions to update the firestore docs to clock in etc ------------

    const getTodayDocRef = (userId: string) => {

        const today = getTimeZoneStringFromDate(new Date());

        const docId = `${userId}_${today}`;
        return doc(db, "time_entries", docId);
    }

    const getLocationSuccessMessage = (
        userId: string,
        action: KioskTimeAction,
        fallbackMessage: string,
        deviceLocation?: string
    ): string => {
        const today = getTimeZoneStringFromDate(new Date());
        const location = deviceLocation || employeeScheduleMap[userId]?.[today]?.location;
        if (!location) return fallbackMessage;

        const messages = locationSuccessMessages[getScheduleLocationLabel(location)]?.[action];
        if (!messages?.length) return fallbackMessage;

        return messages[Math.floor(Math.random() * messages.length)];
    };

    //takes a given date object and returns it in MST 
    const getTimeZoneStringFromDate = (convertDate: Date): string => {
        const now = convertDate
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'America/Denver', // MST/MDT (with DST handling)
        };

        // format: MM/DD/YYYY
        const formatter = new Intl.DateTimeFormat('en-US', options);
        const parts = formatter.formatToParts(now);
        const year = parts.find(p => p.type === 'year')?.value;
        const month = parts.find(p => p.type === 'month')?.value;
        const day = parts.find(p => p.type === 'day')?.value;
        const today = `${year}-${month}-${day}`; // "YYYY-MM-DD" format
        return today
    }


    async function clockIn(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const todayDateString = getTimeZoneStringFromDate(new Date());

        try {
            const access = await getClockInAccess(db, false);

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    showKioskMessage("Unable to verify this kiosk. Please try again.", "Kiosk unavailable", "error");
                } else {
                    showKioskMessage("This device is not an approved office kiosk.", "Clock-in blocked", "error");
                }
                return;
            }

            const clockInEvent: TimeEvent = {
                type: "clock_in",
                timestamp: now,
                source: access.source,
                ...(access.deviceId ? { deviceId: access.deviceId } : {}),
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    // Create the doc if it doesn't exist yet
                    await setDoc(docRef, {
                        userId,
                        date: todayDateString,
                        events: [clockInEvent],
                    });
                } else {
                    // Add a new event to the existing array
                    await updateDoc(docRef, {
                        events: arrayUnion(clockInEvent),
                    });
                }
            });
            showKioskMessage(
                getLocationSuccessMessage(userId, "clock_in", "You are now clocked in! Godspeed.", access.deviceLocation),
                "You’re clocked in",
                "success"
            )

        } catch (error) {
            alert("Failed to clock in please try again")
            // Optionally show error message to user
        }
    }

    async function takeBreak(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const todayDateString = getTimeZoneStringFromDate(new Date());

        try {
            const access = await getClockInAccess(db, false);

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    showKioskMessage("Unable to verify this kiosk. Please try again.", "Kiosk unavailable", "error");
                } else {
                    showKioskMessage("This device is not an approved office kiosk.", "Break blocked", "error");
                }
                return;
            }

            const breakStartEvent: TimeEvent = {
                type: "break_start",
                timestamp: now,
                source: access.source,
                deviceId: access.deviceId,
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                await updateDoc(docRef, {
                    events: arrayUnion(breakStartEvent)
                });
            });
            showKioskMessage(
                getLocationSuccessMessage(userId, "break_start", "You are now on break. You deserve it!", access.deviceLocation),
                "Break started",
                "success"
            )
        } catch (error) {
            alert("Break start failed, please try again")
        }


    }

    async function endBreak(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const todayDateString = getTimeZoneStringFromDate(new Date());
        try {
            const access = await getClockInAccess(db, false);

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    showKioskMessage("Unable to verify this kiosk. Please try again.", "Kiosk unavailable", "error");
                } else {
                    showKioskMessage("This device is not an approved office kiosk.", "Return blocked", "error");
                }
                return;
            }

            const breakEndEvent: TimeEvent = {
                type: "break_end",
                timestamp: now,
                source: access.source,
                deviceId: access.deviceId,
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                await updateDoc(docRef, {
                    events: arrayUnion(breakEndEvent)
                });
            });
            showKioskMessage(
                getLocationSuccessMessage(userId, "break_end", "You are now off break. All good things come to an end.", access.deviceLocation),
                "Welcome back",
                "success"
            )
        } catch (error) {
            alert("Break end failed, please try again")
        }

    }


    async function clockOut(userId: string) {
        const docRef = getTodayDocRef(userId);
        const now = Timestamp.now();
        const todayDateString = getTimeZoneStringFromDate(new Date());
        try {
            const access = await getClockInAccess(db, false);

            if (!access.allowed) {
                if (access.reason === "settings_unavailable") {
                    showKioskMessage("Unable to verify this kiosk. Please try again.", "Kiosk unavailable", "error");
                } else {
                    showKioskMessage("This device is not an approved office kiosk.", "Clock-out blocked", "error");
                }
                return;
            }

            const clockOutEvent: TimeEvent = {
                type: "clock_out",
                timestamp: now,
                source: access.source,
                deviceId: access.deviceId,
                ...(access.deviceLocation ? { deviceLocation: access.deviceLocation } : {}),
            };

            await updateAccruedMinutesForDate(db, userId, todayDateString, async () => {
                await updateDoc(docRef, {
                    events: arrayUnion(clockOutEvent)
                });
            });
            showKioskMessage(
                getLocationSuccessMessage(userId, "clock_out", "You are now clocked out. Enjoy your time off!", access.deviceLocation),
                "You’re clocked out",
                "success"
            )
        } catch (error) {
            alert("clock out failed, please try again")
        }

    }

    const handleSelectRandomEmployee = (emp: Employee) => {
        setCurrentStatus("Checking status…")
        getCurrentStatusByEmployee(emp.id)
        setClockInEmployee(emp.id)
        let fullName = emp.firstName + " " + emp.lastName
        setFullName(fullName)
        setNotScheduled(false)
        setClockInEmployee(emp.id)
    }

    const todayDateString = currentTime.format("YYYY-MM-DD");
    const todaysEmployees = employees.filter((employee) =>
        employeeScheduleMap[employee.id]?.[todayDateString]
    ).sort((firstEmployee, secondEmployee) =>
        firstEmployee.firstName.localeCompare(secondEmployee.firstName)
    );

    const getShiftClassName = (shift: EmployeeSchedule): string => {
        if (shift.location === "Time Off" || shift.location === "Pto") {
            return "time-off";
        }

        if (shift.location === "Coin Show") {
            return "coin-show";
        }

        if (shift.location === "In Store - West wing") {
            return "west-wing";
        }

        if (getScheduleLocationLabel(shift.location) === "In Store - Castle") {
            return "castle";
        }

        return "";
    };

    const isTimeOffShift = (shift: EmployeeSchedule): boolean =>
        shift.location === "Time Off" || shift.location === "Pto";

    return (
        <div className="kiosk-page">
            <div className="kiosk-container">
                <main>
                    <section className="today-schedule" aria-labelledby="today-heading">
                        <div className="section-heading">
                            <div>
                                <span className="section-kicker">Today</span>
                                <h2 id="today-heading">{currentTime.format("dddd, MMMM D")}</h2>
                            </div>
                            <button className="unscheduled-button" onClick={() => setNotScheduled(true)}>
                                <span className="unscheduled-button-icon" aria-hidden="true" />
                                I’m not on today’s schedule
                            </button>
                        </div>

                        {scheduleLoading ? (
                            <div className="schedule-loading-state" aria-live="polite">
                                <span className="loading-dot" />
                                <span>Loading today’s schedule…</span>
                            </div>
                        ) : todaysEmployees.length > 0 ? (
                            <div className="today-employee-grid">
                                {todaysEmployees.map((employee) => {
                                    const shift = employeeScheduleMap[employee.id][todayDateString];
                                    const timeOff = isTimeOffShift(shift);
                                    const shiftClassName = getShiftClassName(shift);

                                    return (
                                        <button
                                            key={employee.id}
                                            className={`today-employee-card ${shiftClassName}`.trim()}
                                            onClick={() => handleOpenPinDialog(employee)}
                                            disabled={timeOff}
                                        >
                                            <span className="employee-avatar" aria-hidden="true">
                                                {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                                            </span>
                                            <span className="today-shift-details">
                                                <span className="today-employee-name">{employee.firstName} {employee.lastName}</span>
                                                <span className="today-shift-time">
                                                    {timeOff ? "Time off" : `${formatDate(shift.shiftStart)} – ${formatDate(shift.shiftEnd)}`}
                                                </span>
                                                {!timeOff && <span className="today-shift-location">{getScheduleLocationLabel(shift.location)}</span>}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-today-state">
                                <span aria-hidden="true">☀</span>
                                <div>
                                    <h3>No one is scheduled today</h3>
                                    <p>If you’re working, use the button above to find your name.</p>
                                </div>
                            </div>
                        )}
                    </section>

                    <section className="upcoming-schedule" aria-labelledby="upcoming-heading">
                        <div className="section-heading upcoming-heading">
                            <div>
                                <span className="section-kicker">Calendar</span>
                                <h2 id="upcoming-heading">Upcoming schedule</h2>
                            </div>
                            <p>Swipe sideways to see more days.</p>
                        </div>

                        <div className="schedule-date-list">
                            {dateList.map((date) => {
                                const dateStr = date.format("YYYY-MM-DD");
                                const isToday = date.isSame(currentTime, "day");
                                const employeesWithShifts = employees
                                    .filter((employee) => employeeScheduleMap[employee.id]?.[dateStr])
                                    .sort((firstEmployee, secondEmployee) =>
                                        firstEmployee.firstName.localeCompare(secondEmployee.firstName)
                                    );

                                if (date.day() === 0) return null;

                                return (
                                    <article key={dateStr} className={`date-card ${isToday ? "today-date-card" : ""}`.trim()}>
                                        <div className="date-card-header">
                                            <span>{date.format("ddd")}</span>
                                            <strong>{date.format("D")}</strong>
                                            <span>{date.format("MMM")}</span>
                                        </div>

                                        {employeesWithShifts.length > 0 ? (
                                            <div className="shift-list">
                                                {employeesWithShifts.map((employee) => {
                                                    const shift = employeeScheduleMap[employee.id][dateStr];
                                                    const timeOff = isTimeOffShift(shift);

                                                    return (
                                                        <div key={employee.id} className={`schedule-shift ${getShiftClassName(shift)}`.trim()}>
                                                            <span className="shift-name">{employee.firstName}</span>
                                                            <span className="shift-time">
                                                                {timeOff ? "Time off" : `${formatDate(shift.shiftStart)}–${formatDate(shift.shiftEnd)}`}
                                                            </span>
                                                            {!timeOff && <span className="shift-location">{getScheduleLocationLabel(shift.location)}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="no-shifts">No shifts</p>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    </section>
                </main>
            </div>

            <SelectEmployeeDialog
                open={notScheduled}
                onCancel={() => setNotScheduled(false)}
                employees={employees}
                onSubmit={(emp) => handleSelectRandomEmployee(emp)}
            />
            <PinDialog
                open={clockInEmployee !== ""}
                onClose={() => setClockInEmployee("")}
                onSubmit={handleSubmitPin}
                fullName={fullName}
                currentStatus={currentStatus}
            />

            <Snackbar
                open={message !== ""}
                autoHideDuration={8000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                className="kiosk-message-snackbar"
            >
                <div className={`kiosk-message-card ${messageKind}`} role={messageKind === "error" ? "alert" : "status"}>
                    <span className="kiosk-message-icon" aria-hidden="true">
                        {messageKind === "success" ? "✓" : "!"}
                    </span>
                    <div className="kiosk-message-copy">
                        <strong>{messageTitle}</strong>
                        <p>{message}</p>
                    </div>
                    <button className="dismiss-kiosk-message" onClick={handleClose} aria-label="Dismiss message">×</button>
                </div>
            </Snackbar>

        </div>
    );
};

require('./styles.scss')
